import type { StrategyNode } from './BaseNode';
import { v4 as uuid } from 'uuid';
import { isStrategyNode } from './typeGuards';
import { reinstantiate } from '../utils/nodeFactory';

export class PnLNode implements StrategyNode {
    id: string;
    type: 'PnL' = 'PnL';
    title?: string;
    isManualRename?: boolean;
    isTopLevel = true;
    config: {
        entry?: StrategyNode;
        exit?: StrategyNode;
        maxLots: number;
        lotSize: number;
        fees: number;
        mode: 'long' | 'short';
        exitMode?: 'signal' | 'hold';
        holdDuration?: number;
        holdType?: 'minute' | 'hour' | 'day';
    };

    constructor(
        id: string,
        title?: string,
        entry?: StrategyNode,
        exit?: StrategyNode,
        maxLots: number = 1,
        lotSize: number = 1,
        fees: number = 0,
        mode: 'long' | 'short' = 'long',
        isManualRename?: boolean,
        exitMode: 'signal' | 'hold' = 'signal',
        holdDuration: number = 1,
        holdType: 'minute' | 'hour' | 'day' = 'hour'
    ) {
        this.id = id;
        this.title = title || 'Strategy PnL';
        this.isManualRename = isManualRename;
        this.config = { 
            entry: entry ? reinstantiate(entry) : undefined, 
            exit: exit ? reinstantiate(exit) : undefined, 
            maxLots, 
            lotSize, 
            fees, 
            mode, 
            exitMode, 
            holdDuration, 
            holdType 
        };
    }

    update(id: string, patch: StrategyNode): StrategyNode {
        // If the patch is meant for this node itself
        if (this.id === id) {
            return patch;
        }

        // Recursively update entry/exit
        const updatedEntry = this.config.entry?.update(id, patch);
        const updatedExit = this.config.exit?.update(id, patch);

        if (updatedEntry !== this.config.entry || updatedExit !== this.config.exit) {
            return new PnLNode(
                this.id,
                this.title,
                updatedEntry,
                updatedExit,
                this.config.maxLots,
                this.config.lotSize,
                this.config.fees,
                this.config.mode,
                this.isManualRename,
                (patch as any).config?.exitMode ?? this.config.exitMode,
                (patch as any).config?.holdDuration ?? this.config.holdDuration,
                (patch as any).config?.holdType ?? this.config.holdType
            );
        }

        return this;
    }

    clone(): StrategyNode {
        const cloned = new PnLNode(
            uuid(),
            this.title,
            isStrategyNode(this.config.entry) ? this.config.entry.clone() : undefined,
            isStrategyNode(this.config.exit) ? this.config.exit.clone() : undefined,
            this.config.maxLots,
            this.config.lotSize,
            this.config.fees,
            this.config.mode,
            this.isManualRename,
            this.config.exitMode,
            this.config.holdDuration,
            this.config.holdType
        );
        return cloned;
    }

}
